import {useState, useMemo} from "react";
import {
    ClearColor, NavItem, NavWrapper,
    RecordsSummary,
    StatsTable, StatsWrap,
    TableCell,
    TableHeader, TableMidlRow,
    TableRow,
    Wrapper
} from "../styled";

export const IndustryStats = ({statistics = []}) => {
    const industryStats = useMemo(() => {
        return statistics.filter(stat => !stat?.item?.title?.match(/^\d{4}$/));
    }, [statistics]);

    const industries = industryStats.map(stat => stat.item.title);
    const [activeIndustry, setActiveIndustry] = useState(industries[0] || '');

    const currentIndustryData = useMemo(() => {
        const activeStat = industryStats.find(stat => stat.item.title === activeIndustry);
        if (!activeStat?.item?.records) return null;

        const records = activeStat.item.records;
        const columns = records.columns || [];
        const rows = records.rows || [];

        return {
            recordTitle: records.record_title || '',
            recordCount: records.record_count || 0,
            columns,
            rows
        };
    }, [activeIndustry, industryStats]);

    const hasInterquartileRange = useMemo(() => {
        if (!currentIndustryData?.columns) return false;
        const columnIds = currentIndustryData.columns.map(col => col.id);
        return columnIds.includes('lowerQ') && columnIds.includes('median') && 
               columnIds.includes('avg') && columnIds.includes('upperQ');
    }, [currentIndustryData?.columns]);

    if (industryStats.length === 0 || !currentIndustryData) {
        return null;
    }

    const getColumnValue = (row, columnId) => {
        if (columnId === 'name' || columnId === 'category') {
            return row[columnId] || row.name || row.category || '';
        }
        return row[columnId] !== null && row[columnId] !== undefined ? row[columnId] : '';
    };

    const formatValue = (value, column) => {
        if (value === null || value === undefined || value === '') return '';
        if (column.type === 'number') {
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            if (isNaN(numValue)) return value;
            return numValue.toFixed(1);
        }
        return value;
    };

    const formatDisclosed = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return `(${value})`;
    };

    const nameColumn = currentIndustryData.columns.find(col => col.id === 'category' || col.id === 'name');
    const disclosedColumn = currentIndustryData.columns.find(col => col.id === 'disclosed');
    const numericColumns = currentIndustryData.columns.filter(col => 
        col.id !== 'category' && col.id !== 'name' && col.id !== 'disclosed'
    );

    if (hasInterquartileRange && nameColumn && disclosedColumn) {
        const unit = numericColumns[0]?.unit || 'years';
        return (
            <Wrapper>
                <NavWrapper>
                    {industries.map((industry) => (
                        <NavItem
                            key={industry}
                            active={industry === activeIndustry}
                            onClick={() => setActiveIndustry(industry)}
                        >
                            {industry}
                        </NavItem>
                    ))}
                </NavWrapper>

                <RecordsSummary>
                    {currentIndustryData.recordTitle || 'Records Found'}
                    <ClearColor>{currentIndustryData.recordCount}</ClearColor>
                </RecordsSummary>

                <StatsWrap>
                    <StatsTable>
                        <thead>
                            <TableHeader>
                                <TableCell as="th">{nameColumn.label}</TableCell>
                                <TableCell as="th">{disclosedColumn.label}</TableCell>
                                <TableCell as="th" colSpan={numericColumns.length} align="center">
                                    Interquartile range ({unit})
                                </TableCell>
                            </TableHeader>
                        </thead>
                        <tbody>
                            <TableMidlRow>
                                <TableCell as="th"></TableCell>
                                <TableCell as="th" align="center"></TableCell>
                                {numericColumns.map((column) => (
                                    <TableCell key={column.id} as="th" align="center">
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableMidlRow>
                            {currentIndustryData.rows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{getColumnValue(row, nameColumn.id)}</TableCell>
                                    <TableCell align="center">
                                        {formatDisclosed(getColumnValue(row, disclosedColumn.id))}
                                    </TableCell>
                                    {numericColumns.map((column) => (
                                        <TableCell key={column.id} align="center">
                                            {formatValue(getColumnValue(row, column.id), column)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </tbody>
                    </StatsTable>
                </StatsWrap>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <NavWrapper>
                {industries.map((industry) => (
                    <NavItem
                        key={industry}
                        active={industry === activeIndustry}
                        onClick={() => setActiveIndustry(industry)}
                    >
                        {industry}
                    </NavItem>
                ))}
            </NavWrapper>

            <RecordsSummary>
                {currentIndustryData.recordTitle || 'Records Found'}
                <ClearColor>{currentIndustryData.recordCount}</ClearColor>
            </RecordsSummary>

            <StatsWrap>
                <StatsTable>
                    <thead>
                        <TableHeader>
                            {currentIndustryData.columns.map((column) => (
                                <TableCell key={column.id} as="th" align={column.id === 'disclosed' ? 'center' : 'left'}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableHeader>
                    </thead>
                    <tbody>
                        {currentIndustryData.rows.map((row, index) => (
                            <TableRow key={index}>
                                {currentIndustryData.columns.map((column) => {
                                    const value = getColumnValue(row, column.id);
                                    const isNameColumn = column.id === 'name' || column.id === 'category';

                                    return (
                                        <TableCell
                                            key={column.id}
                                            indented={row.indent > 0}
                                            align={column.id === 'disclosed' ? 'center' : 'left'}
                                        >
                                            {isNameColumn
                                                ? value
                                                : column.id === 'disclosed'
                                                    ? formatDisclosed(value)
                                                    : formatValue(value, column)
                                            }
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </tbody>
                </StatsTable>
            </StatsWrap>
        </Wrapper>
    );
};

